import { getErrorString } from "#src/utils/getErrorString.ts";
import { getToken } from "#src/utils/getToken.ts";
import type { TypedDocumentNode } from "#types/graphql.d.ts";
import {
  InfiniteData,
  keepPreviousData,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import type { DocumentNode } from "graphql";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    },
  },
});

interface UseApiInput<TData, TVariables> {
  queryKey?: QueryKey;
  variables: TVariables;
  headers?: { [k: string]: string };
  skip?: boolean;
  name?: string;
  token?: string;
  refetchOnWindowFocus?: boolean;
  refetchInterval?: number;
  onSuccess?: (data: TData) => void;
}

interface UseApiMutationInput<
  TData,
  TVariables = any,
> extends UseMutationOptions<TData> {
  variables?: TVariables;
  headers?: { [k: string]: string };
}

export type UseInfiniteApiResult<TData = any, TVariables = any> = Omit<
  UseInfiniteQueryResult<InfiniteData<TData>, TVariables>,
  "data"
> & {
  loading: boolean;
  count: number;
} & TData;

export type UseApiResult<TData = any, TVariables = any> = Omit<
  UseQueryResult<TData, TVariables>,
  "data"
> & {
  loading: boolean;
} & TData;

export type UseApiMutationResult<TData, TVariables = any> = (args?: {
  variables: TVariables;
}) => Promise<TData>;

export function useApiMutation<TData = any, TVariables = any>(
  query: string | DocumentNode | TypedDocumentNode<TData, TVariables>,
  config?: UseApiMutationInput<TData, TVariables>
): UseApiMutationResult<TData, TVariables> {
  const mutationObject = useMutation<TData, any, { variables: TVariables }>({
    mutationFn: (args) => {
      // console.log("args", args);
      // console.log("config", config);

      const variables = {
        ...args?.variables,
        ...config?.variables,
      };

      // console.log("variables", variables);

      return fetchApi(query, { variables });
    },
  });
  // console.log("mutationObject", mutationObject);
  // const mutationFunction = ({ variables, config }) => {
  //   console.log("variables", variables);
  //   return mutationObject(variables);
  // };

  return function mutate(args) {
    return new Promise((resolve, reject) => {
      mutationObject.mutate(args, {
        onSuccess: (data, _variables, _context) => {
          resolve(data);
        },
        onError: (error, _variables, _context) => {
          reject(error);
        },
      });
    });
  };
}

export function useApi<TData = any, TVariables = any>(
  query: string | DocumentNode | TypedDocumentNode<TData, TVariables>,
  config: UseApiInput<TData, TVariables> = null
) {
  try {
    const queryKey = getKey(query, config);

    const queryObject = useQuery<TData & { count?: number }, string>({
      queryKey,
      queryFn: (): Promise<TData> => {
        return fetchApi(query, config);
      },
      enabled: !config?.skip,
      refetchOnWindowFocus: config?.refetchOnWindowFocus,
      refetchInterval: config?.refetchInterval,
    });

    // console.log("queryObject", queryObject);
    // console.log("queryKey", queryKey);
    // console.log("queryObject.data", queryObject.data);

    return {
      loading: queryObject.isLoading && !queryObject.isRefetching,
      ...queryObject,
      data: null,
      count: queryObject.data?.count,
      ...queryObject.data,
    };
  } catch (e) {
    console.error(e);
    // return {} as any;
    throw e;
  }
}

const getDocKey = (query: DocumentNode): string => {
  return (query.definitions[0] as any).name.value;
};

const getOperationKey = (query: string | DocumentNode): string => {
  if (typeof query !== "string") {
    return getDocKey(query);
  }

  return (
    query.match(
      /^(?:query|mutation|subscription)\s+([_A-Za-z][_0-9A-Za-z]*)/
    )?.[1] ?? query
  );
};

export const dedupeGraphqlFragments = (query: string): string => {
  const seenFragments = new Map<string, string>();
  const removals: Array<{ start: number; end: number }> = [];
  let index = 0;

  while (index < query.length) {
    index = skipGraphqlIgnored(query, index);

    if (!isGraphqlWordAt(query, index, "fragment")) {
      index++;
      continue;
    }

    const fragment = readGraphqlFragmentDefinition(query, index);

    if (!fragment) {
      index++;
      continue;
    }

    const existingFragment = seenFragments.get(fragment.name);

    if (existingFragment === fragment.source) {
      removals.push({ start: fragment.start, end: fragment.end });
    } else if (!existingFragment) {
      seenFragments.set(fragment.name, fragment.source);
    }

    index = fragment.end;
  }

  return removals.reduceRight((result, removal) => {
    return result.slice(0, removal.start) + result.slice(removal.end);
  }, query);
};

export const getPrintedQuery = (query: string | DocumentNode): string => {
  if (typeof query === "string") {
    return dedupeGraphqlFragments(query);
  }

  const printedQuery = query.loc?.source.body;

  if (!printedQuery) {
    throw new Error(
      "GraphQL query must be a string or a DocumentNode with loc.source.body"
    );
  }

  return dedupeGraphqlFragments(printedQuery);
};

function readGraphqlFragmentDefinition(query: string, start: number) {
  let index = start + "fragment".length;
  index = skipGraphqlIgnored(query, index);

  const nameStart = index;

  if (!isGraphqlNameStart(query[index])) {
    return null;
  }

  index++;

  while (isGraphqlNameContinue(query[index])) {
    index++;
  }

  const name = query.slice(nameStart, index);
  const selectionStart = findNextGraphqlSelectionStart(query, index);

  if (selectionStart === -1) {
    return null;
  }

  const selectionEnd = findMatchingGraphqlBrace(query, selectionStart);

  if (selectionEnd === -1) {
    return null;
  }

  const end = selectionEnd + 1;

  return {
    end,
    name,
    source: query.slice(start, end),
    start,
  };
}

function findNextGraphqlSelectionStart(query: string, start: number) {
  let index = start;

  while (index < query.length) {
    index = skipGraphqlIgnored(query, index);

    if (query[index] === '"') {
      index = skipGraphqlString(query, index);
      continue;
    }

    if (query[index] === "{") {
      return index;
    }

    index++;
  }

  return -1;
}

function findMatchingGraphqlBrace(query: string, start: number) {
  let depth = 0;
  let index = start;

  while (index < query.length) {
    index = skipGraphqlIgnored(query, index);

    if (query[index] === '"') {
      index = skipGraphqlString(query, index);
      continue;
    }

    if (query[index] === "{") {
      depth++;
    } else if (query[index] === "}") {
      depth--;

      if (!depth) {
        return index;
      }
    }

    index++;
  }

  return -1;
}

function skipGraphqlIgnored(query: string, start: number) {
  let index = start;

  while (index < query.length) {
    if (/\s|,/.test(query[index])) {
      index++;
      continue;
    }

    if (query[index] === "#") {
      const lineEnd = query.indexOf("\n", index + 1);
      index = lineEnd === -1 ? query.length : lineEnd + 1;
      continue;
    }

    return index;
  }

  return index;
}

function skipGraphqlString(query: string, start: number) {
  if (query.startsWith('"""', start)) {
    const blockEnd = query.indexOf('"""', start + 3);

    return blockEnd === -1 ? query.length : blockEnd + 3;
  }

  let index = start + 1;

  while (index < query.length) {
    if (query[index] === "\\") {
      index += 2;
      continue;
    }

    if (query[index] === '"') {
      return index + 1;
    }

    index++;
  }

  return query.length;
}

function isGraphqlWordAt(query: string, index: number, word: string) {
  return (
    query.startsWith(word, index) &&
    !isGraphqlNameContinue(query[index - 1]) &&
    !isGraphqlNameContinue(query[index + word.length])
  );
}

function isGraphqlNameStart(char: string | undefined) {
  return !!char && /[A-Za-z_]/.test(char);
}

function isGraphqlNameContinue(char: string | undefined) {
  return !!char && /[0-9A-Za-z_]/.test(char);
}

function getKey(query, config, infinite = false) {
  const operation = getOperationKey(query);
  let key = [operation];

  if (config?.variables) {
    if (config.variables.filter) {
      key.push(config.variables.filter);
    }

    if (config.variables.where) {
      if (config.variables.where.id) {
        key.push(config.variables.where?.id);
      }

      if (config.variables.where.date) {
        key.push(config.variables.where.date.equals);
      }

      if (config.variables.where.organizationId) {
        key.push(config.variables.where.organizationId);
      }

      if (config.variables.where.q) {
        key.push(config.variables.where.q);
      }

      // key.push(JSON.stringify(config.variables.where));
    }

    if (config.variables.id) {
      key.push(config.variables.id);
    }

    // console.log("key", key);
  }

  if (infinite) {
    key.push("infinite");
  }

  if (config?.queryKey) {
    key = config.queryKey;
  }

  return key;
}

export function useInfiniteApi<TData = any, TVariables = any>(
  query: string | DocumentNode | TypedDocumentNode<TData, TVariables>,
  config: UseApiInput<TData, TVariables> = null
) {
  // UseInfiniteApiResult<TData, TVariables>
  const queryKey = getKey(query, config, true);

  // console.log("useInfiniteApi queryKey", queryKey);

  const queryObject = useInfiniteQuery<TData, string>({
    queryKey,
    initialPageParam: 0,
    queryFn: async (pageArgs): Promise<TData> => {
      // console.log("pageArgs", pageArgs);
      const variables = {
        ...config,
        variables: {
          ...config.variables,
          skip: pageArgs.pageParam || 0,
          take: (config.variables as any)?.take || 10,
        },
      };

      // console.log("variables", variables);

      return await fetchApi(query, variables);
    },
    getNextPageParam: (lastPage, allPages) => {
      const mainKey = Object.keys(lastPage).filter((k) => k !== "count")[0];

      const items = allPages.reduce((acc, p) => {
        if (Array.isArray(p[mainKey])) {
          return acc.concat(p[mainKey]);
        } else {
          for (const k in p[mainKey]) {
            if (Array.isArray(p[mainKey][k])) {
              return acc.concat(p[mainKey][k]);
            }
          }
        }

        return acc;
      }, []);

      let param;
      const pageSize = (config.variables as any)?.take || 10;

      if (Array.isArray(lastPage[mainKey])) {
        param =
          lastPage[mainKey]?.length >= pageSize ? items.length : undefined;
      } else {
        for (const k in lastPage[mainKey]) {
          if (param) {
            break;
          }

          if (Array.isArray(lastPage[mainKey][k])) {
            param =
              lastPage[mainKey][k]?.length >= pageSize
                ? items.length
                : undefined;
          }
        }
      }

      // console.log("allPages", allPages);
      // console.log("lastPage", lastPage);
      // console.log("param", param);

      return param;
    },
    enabled: !config?.skip,
    refetchInterval: config?.refetchInterval,
  });

  const data = queryObject.data?.pages?.reduce((acc, p) => {
    const mainKey = Object.keys(p).filter((k) => k !== "count")[0];

    if (Array.isArray(p[mainKey])) {
      if (!acc[mainKey]) {
        acc[mainKey] = [];
      }

      acc[mainKey] = acc[mainKey].concat(p[mainKey]);
    } else {
      if (!acc[mainKey]) {
        acc[mainKey] = {};
      }

      for (const k in p[mainKey]) {
        if (Array.isArray(p[mainKey][k])) {
          if (!acc[mainKey][k]) {
            acc[mainKey][k] = [];
          }
          acc[mainKey][k] = acc[mainKey][k].concat(p[mainKey][k]);
        } else {
          acc[mainKey][k] = p[mainKey][k];
        }
      }
    }

    return acc;
  }, {} as TData);

  // console.log("data", data);

  let count = 0;

  if (
    queryObject.data?.pages &&
    queryObject.data?.pages[0] &&
    queryObject.data?.pages[0]["count"]
  ) {
    count = queryObject.data?.pages[0]["count"];
  }

  return {
    ...queryObject,
    loading: queryObject.isLoading,
    count,
    ...data,
  };
}

export async function apiClient<TData, TVariables = any>(
  query: string | DocumentNode | TypedDocumentNode<TData, TVariables>,
  config: UseApiInput<TData, TVariables> & {
    onSuccess?: (data: TData) => void;
  } = null
): Promise<TData> {
  const queryKey = getKey(query, config);
  // console.log("queryKey", queryKey);
  // console.log("queryKey", queryKey);

  if (config?.skip) {
    return null;
  }

  return queryClient.fetchQuery({
    queryKey,
    queryFn: async () => fetchApi(query, config),
  });
}

async function fetchApi<TData, TVariables = any>(
  query: string | DocumentNode | TypedDocumentNode<TData, TVariables>,
  config:
    | (UseApiInput<TData, TVariables> & { onSuccess?: (data: TData) => void })
    | (UseApiMutationInput<TVariables> & { onSuccess?: (data: TData) => void })
): Promise<TData> {
  // console.log("query", query);
  // console.log("config", config);

  const operation = getOperationKey(query as string | DocumentNode);
  const url = `${process.env.NEXT_PUBLIC_APP_ENDPOINT}?op=${operation}`;
  const printedQuery = getPrintedQuery(query as string | DocumentNode);
  const body = {
    query: printedQuery,
  };

  if (config?.variables && Object.keys(config.variables)?.length) {
    body["variables"] = config.variables;
  }

  try {
    let idToken = getToken();

    if (
      config?.variables?.token &&
      !printedQuery.includes("checkResetPasswordToken")
    ) {
      idToken = config.variables.token;
      delete body["variables"]["token"];
    }

    const headers = { "Content-Type": "application/json" };

    if (idToken) {
      headers["Authorization"] = `Bearer ${idToken}`;
    }

    if (config?.headers?.host) {
      headers["host"] = config?.headers?.host;
    }

    // if (printedQuery.includes("findFilters")) {
    //   throw new TypeError("Load failed");
    // }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      // signal: AbortSignal.timeout(10 * 1000),
    });

    const count = response.headers.get("count")
      ? parseInt(response.headers.get("count"))
      : 0;
    const json = await response.json();

    if (json.errors?.length) {
      throw new Error(json.errors[0]) as any;
    }

    const result = {
      ...json.data,
      count,
    };

    if (config?.onSuccess) {
      config.onSuccess(result);
    }

    return result;
  } catch (e) {
    console.error(e);
    // console.log("e.message", e.message);
    const error = getErrorString(e);

    if (
      error === "Unauthorized" &&
      (location?.pathname.includes("/admin") ||
        location?.pathname.includes("/super-admin") ||
        location?.pathname.includes("/account")) &&
      !location?.pathname.includes("/kiosk") &&
      !location?.pathname.includes("/cart") &&
      !location?.hostname.includes("demo.")
    ) {
      window.location.assign(
        `/login?next=${location?.pathname}&reason=unauthorized`
      );
    }

    throw e;
  }
}

export function withApi(query, config) {
  return (Component) => {
    return function WrappedWithApi(props) {
      // console.log("config", config);
      const hocOptions = config.options ? config.options(props) : {};

      const queryKey = hocOptions?.queryKey
        ? hocOptions.queryKey
        : getKey(query, hocOptions);

      const queryObject = useApi(query, {
        ...config,
        variables: hocOptions?.variables,
        skip: config.skip ? config.skip(props) : false,
        queryKey,
      });

      const queryProps = {
        [config.name]: queryObject,
      };

      return <Component {...props} {...queryProps} />;
    };
  };
}

export function withApiMutation(query, config) {
  return (Component) => {
    return function WrappedWithApi(props) {
      const mutationFunction = useApiMutation(query, {
        variables: config?.options ? config.options(props).variables : {},
        ...config,
      });

      const mutationProps = {
        [config.name]: mutationFunction,
      };

      return <Component {...props} {...mutationProps} />;
    };
  };
}
